import { toast } from '@heidi/design-system/toast';

import {
  GET_DOCUMENT_BY_ID,
  type QueryDocumentResponse,
} from '@/hooks/query/useQueryDocumentById';
import { track } from '@/lib/analytics/track';
import { type NoteGenerateTrigger } from '@/lib/telemetry/noteGenerateTracking';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { type Dispatch } from '@/store';
import { paywallModalOpenFromQuotaExceeded } from '@heidi/analytics/events';
import {
  delayUpgradeExtendAction,
  QuotaExceedType,
  shouldShowDelayUpgradeModal,
} from '@heidi/commerce';
import { openQuotaExceededUpgradeModal, useIsFreeUser } from '@heidi/commerce/data';
import type { components } from '@heidi/schema/ml-scribe';
import {
  type Brain,
  type GenerationMethod,
  type TemplateCategory,
} from '@heidi/schema/ml-scribe';
import {
  FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  clearSessionNoteRequestPending,
  documentCreatedByTemplate,
  getSessionFromCache,
  holdDocumentFlowDispatch,
  mapNewBrain,
  markSessionNoteRequestPending,
  setConsultNoteAsFailed,
} from '@heidi/session/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryUser } from '../query/useQueryUser';
import { useMutateCreateEmptyDocument } from './useMutateCreateEmptyDocument';
import { useMutateRegenerateDocument } from './useMutateRegenerateDocument';

type ErrorResponse = components['schemas']['ErrorResponse'];

type Props = {
  templateId?: string;
  templateCategory?: TemplateCategory;
  aiCommandId?: string;
  aiCommandText?: string;
  generationMethod: GenerationMethod;
  brain?: Brain;
  name?: string;
  queueOnly?: boolean;
  /** `autonomous` after a stop or upload, `explicit` for a user action. */
  trigger: NoteGenerateTrigger;
};

export default function useMutateCreateDocumentFromNote({
  sessionId,
}: {
  sessionId: string;
}) {
  const dispatch = useDispatch<Dispatch>();
  const { isFreeUser } = useIsFreeUser();

  const { data: userData } = useQueryUser((data) => ({
    brain: mapNewBrain(data?.session_settings?.brain, isFreeUser),
    voiceStyle: data?.session_settings?.default_voice_style,
  }));

  const { mutateAsync: createEmptyDocument } = useMutateCreateEmptyDocument({
    sessionId,
  });
  const { mutateAsync: generateDocumentContent } = useMutateRegenerateDocument({
    sessionId,
  });
  const setDelayUpgradeModalOpen = useSetAtom(shouldShowDelayUpgradeModal);
  const setDelayUpgradeExtendAction = useSetAtom(delayUpgradeExtendAction);

  const queryClient = useQueryClient();
  const documentFlowHolds = useRef(
    new WeakMap<Props, ReturnType<typeof holdDocumentFlowDispatch>>()
  ).current;
  const holdDocumentFlow = useCallback(
    (input: Props): void => {
      if (input.queueOnly || documentFlowHolds.has(input)) return;
      documentFlowHolds.set(input, holdDocumentFlowDispatch(sessionId));
    },
    [documentFlowHolds, sessionId]
  );
  const releaseDocumentFlow = useCallback(
    (input?: Props): void => {
      if (!input) return;
      documentFlowHolds.get(input)?.release();
      documentFlowHolds.delete(input);
    },
    [documentFlowHolds]
  );

  const mutation = useMutation({
    mutationKey: ['createEmptyDocumentFromNote', { sessionId }],
    mutationFn: async (input: Props) => {
      if (!input.queueOnly) markSessionNoteRequestPending(sessionId);
      holdDocumentFlow(input);
      let documentResponse: Awaited<ReturnType<typeof createEmptyDocument>>;
      try {
        documentResponse = await createEmptyDocument({
          ...input,
          brain: userData?.brain,
        });
      } catch (error) {
        releaseDocumentFlow(input);
        if (!input.queueOnly) clearSessionNoteRequestPending(sessionId);
        throw error;
      }

      if (!input.queueOnly) {
        documentFlowHolds
          .get(input)
          ?.bindDocument(documentResponse.documentResponse.data.id);
      }

      if (input.queueOnly) {
        return documentResponse;
      }

      await dispatch.scribeRecordModel._streamResponse({
        sessionID: sessionId,
        generationMethod: input.generationMethod,
        templateId: input.templateId,
        templateCategory: input.templateCategory,
        voiceStyle: userData?.voiceStyle,
        brain: userData?.brain,
        shouldGenerateClinicalTasks: true,
        trigger: input.trigger,
      });

      return documentResponse;
    },
    onSuccess: async ({ documentResponse }, input) => {
      if (input.queueOnly) {
        queryClient.setQueryData<QueryDocumentResponse>(
          [GET_DOCUMENT_BY_ID, { sessionId, documentId: documentResponse.data.id }],
          (data) => {
            if (!data) return data;
            return {
              ...data,
              streamingStatus: StreamingStatus.NOT_STARTED,
              abortController: new AbortController(),
            };
          }
        );
        return;
      }

      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (data) => {
          if (data) {
            return {
              ...data,
              isDocumentFromNote: true,
            };
          }
          return data;
        }
      );

      await generateDocumentContent({
        documentId: documentResponse.data.id,
        brain: documentResponse.data.brain,
        trigger: input.trigger,
      });

      // tracking
      documentCreatedByTemplate(input.templateId);

      // V2
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.DocumentCreated,
        props: {
          session_id: sessionId,
          document_id: documentResponse.data.id,
          generation_method: 'template',
          template_id: input.templateId,
        },
      });
    },
    onSettled: (_data, _error, input) => {
      releaseDocumentFlow(input);
      // void queryClient.invalidateQueries({
      //   queryKey: [
      //     GET_DOCUMENTS_BY_ID,
      //     { sessionId: sessionId },
      //   ],
      // });
    },
    onError: (error, input) => {
      releaseDocumentFlow(input);
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse | undefined;
        // Handle both nested (detail.type) and flat (type) error response formats
        const errorType =
          errorData?.detail?.type ?? (errorData as { type?: string })?.type;
        if (errorType === 'UnlimitedQuotaUntilNotSetError') {
          setDelayUpgradeModalOpen(true);
          setDelayUpgradeExtendAction(QuotaExceedType.CREATE_DOCUMENT_FROM_NOTE);
          return;
        }
        if (
          errorType === 'ASKAIQuotaExceedError' ||
          errorType === 'TemplateQuotaExceedError'
        ) {
          const sessionFromCache = getSessionFromCache(sessionId);
          openQuotaExceededUpgradeModal();
          if ((sessionFromCache?.consult_note.result?.length ?? 0) === 0) {
            setConsultNoteAsFailed(
              sessionId,
              FrontendNoteFailureReason.CREATE_DOC_FROM_NOTE_OUT_OF_QUOTA
            );
          }
          paywallModalOpenFromQuotaExceeded();
          return;
        }
        const errorMsg =
          errorData?.detail?.msg ?? (errorData as { msg?: string })?.msg ?? error.message;
        toast({
          tone: 'destructive',
          title: 'Failed to create document',
          description: errorMsg,
        });
      }
    },
  });

  const mutationMutate = mutation.mutate;
  const mutationMutateAsync = mutation.mutateAsync;
  const mutate = useCallback<typeof mutation.mutate>(
    (input, options) => {
      const invocation = input.queueOnly ? input : { ...input };
      if (!invocation.queueOnly) {
        markSessionNoteRequestPending(sessionId);
        holdDocumentFlow(invocation);
      }
      mutationMutate(invocation, options);
    },
    [holdDocumentFlow, mutationMutate, sessionId]
  );
  const mutateAsync = useCallback<typeof mutation.mutateAsync>(
    (input, options) => {
      const invocation = input.queueOnly ? input : { ...input };
      if (!invocation.queueOnly) {
        markSessionNoteRequestPending(sessionId);
        holdDocumentFlow(invocation);
      }
      return mutationMutateAsync(invocation, options);
    },
    [holdDocumentFlow, mutationMutateAsync, sessionId]
  );

  return { ...mutation, mutate, mutateAsync };
}
